import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  CardActionArea,
  Snackbar,
  Alert,
} from '@mui/material';
import FeedIcon from '@mui/icons-material/Feed';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';
import ElectricMeterRoundedIcon from '@mui/icons-material/ElectricMeterRounded';
import FoundationIcon from '@mui/icons-material/Foundation';
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { brandImageMap, isLoggedIn } from '../../utils/utils';
import { Check } from '@mui/icons-material';
import {
  buy_preference,
  createMatch,
  requestQuote,
  requestQuoteTypeSMS,
} from '../../services/CustomerService';
import { isEmpty } from '../constant';
import LoginMiniForm from '../ui/LoginMiniForm';
import { useNavigate } from 'react-router-dom';
import SiteVisitDialog from './SiteVisitDialog';

function PanelTechnologies({ offer_selection, propertyDetails, userDetails }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedPanelType, setSelectedPanelType] = useState(
    userDetails?.metadata?.buy_preference?.panels?.name || null,
  );
  const [selectedBrand, setSelectedBrand] = useState(
    userDetails?.metadata?.buy_preference?.panels?.recommendedBrand || [],
  );
  const [selectedInverterBrand, setSelectedInverterBrand] = useState(
    userDetails?.metadata?.buy_preference?.inverter?.name || null,
  );
  const [selectedStructureMaterialOption, setSelectedStructureMaterialOption] =
    useState(userDetails?.metadata?.buy_preference?.structure?.name || null);
  const [selectedServicingOption, setSelectedServicingOption] = useState(
    userDetails?.metadata?.buy_preference?.service?.name || null,
  );
  const [quoteStatus, setQuoteStatus] = useState(false);
  const [open, setOpen] = useState(false);
  const [isLoginFormDialogOpen, setIsLoginFormDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isAutoFillComplete, setIsAutoFillComplete] = useState(false);
  const [manualSelectionType, setManualSelectionType] = useState(null);

  const navigate = useNavigate();
  useEffect(() => {
    if (userDetails?.metadata?.buy_preference) {
      setSelectedPanelType(
        userDetails.metadata.buy_preference.panels?.name || null,
      );

      // Handle recommended brands - convert from string names back to brand objects
      const savedBrandNames =
        userDetails.metadata.buy_preference.panels?.recommendedBrand || [];
      if (
        savedBrandNames.length > 0 &&
        offer_selection?.offer_selection?.panels?.brands
      ) {
        const matchedBrands =
          offer_selection.offer_selection.panels.brands.filter((brand) =>
            savedBrandNames.includes(brand.display_name),
          );
        setSelectedBrand(matchedBrands);
      } else {
        setSelectedBrand([]);
      }

      // Handle inverter selection
      const inverterName = userDetails.metadata.buy_preference.inverter?.name;
      if (inverterName && offer_selection?.offer_selection?.inverters?.brands) {
        const savedInverterName = Array.isArray(inverterName)
          ? inverterName[0]
          : inverterName;
        // Create the inverter option object that matches the structure used in the component
        const matchedInverterBrand =
          offer_selection.offer_selection.inverters.brands.find(
            (brand) => brand.display_name === savedInverterName,
          );
        if (matchedInverterBrand) {
          const inverterOption = {
            id: 1, // This will be overwritten when inverterOptions is created
            name: matchedInverterBrand.display_name,
            icon: matchedInverterBrand.logo,
            features: matchedInverterBrand.merits || [],
            tag: (matchedInverterBrand.tag || '').toLowerCase(),
          };

          setSelectedInverterBrand(inverterOption);
        } else {
          setSelectedInverterBrand(null);
        }
      } else {
        setSelectedInverterBrand(null);
      }

      // Handle structure material selection
      const structureName = userDetails.metadata.buy_preference.structure?.name;
      if (
        structureName &&
        offer_selection?.offer_selection?.structure_material
      ) {
        const matchedStructureMaterial =
          offer_selection.offer_selection.structure_material.find(
            (material) => material.display_name === structureName,
          );
        if (matchedStructureMaterial) {
          const structureOption = {
            id: 1, // This will be overwritten when structureMaterialOptions is created
            name: matchedStructureMaterial.display_name,
            description: matchedStructureMaterial.description || '',
            features: matchedStructureMaterial.merits || [],
            tag: (matchedStructureMaterial.tag || '').toLowerCase(),
          };
          setSelectedStructureMaterialOption(structureOption);
        } else {
          setSelectedStructureMaterialOption(null);
        }
      } else {
        setSelectedStructureMaterialOption(null);
      }

      // Handle service selection
      const serviceName = userDetails.metadata.buy_preference.service?.name;
      if (serviceName && offer_selection?.offer_selection?.servicing) {
        const matchedServiceEntry = Object.entries(
          offer_selection.offer_selection.servicing,
        ).find(([key, service]) => service.display_name === serviceName);
        if (matchedServiceEntry) {
          const [serviceKey, serviceValue] = matchedServiceEntry;
          const serviceOption = {
            id: 1, // This will be overwritten when servicingOptions is created
            name: serviceValue.display_name,
            description: serviceValue.description || '',
            features: serviceValue.merits || [],
            tag: (serviceValue.tag || '').toLowerCase(),
          };
          setSelectedServicingOption(serviceOption);
        } else {
          setSelectedServicingOption(null);
        }
      } else {
        setSelectedServicingOption(null);
      }

      // Mark auto-fill as complete to prevent auto-scroll on page load
      setIsAutoFillComplete(true);
    } else {
      // If no saved preferences, mark auto-fill as complete immediately
      setIsAutoFillComplete(true);
    }
  }, [userDetails, offer_selection]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const panelSectionRef = useRef(null);
  const recommendationRef = useRef(null);
  const inverterSectionRef = useRef(null);
  const structureSectionRef = useRef(null);
  const serviceSectionRef = useRef(null);

  const handleQuote = async (vendorId = null, customerId = null) => {
    if (!isEmpty(propertyDetails?.siteVisitData)) {
      const res = await requestQuote(customerId, vendorId);
      const res2 = await requestQuoteTypeSMS(customerId, vendorId);
      await createMatch(customerId, vendorId);
      if (res || res2) {
        setQuoteStatus(true);
        showSnackbar('Quote requested successfully', 'success');
      }
      return;
    }
    setOpen(true);
  };
  const handleRequestCall = async () => {
    setIsLoginFormDialogOpen(true);
  };
  const closeLoginForm = (didUserLogin = false) => {
    setIsLoginFormDialogOpen(false);
    if (didUserLogin) navigate('/dashboard');
  };
  const scrollToSection = (ref) => {
    if (ref.current) {
      const offset = 80;

      setTimeout(() => {
        const elementPosition = ref.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - offset;

        const startPosition = window.scrollY;
        const distance = offsetPosition - startPosition;
        const duration = 1000;
        let start;

        function animation(currentTime) {
          if (!start) start = currentTime;
          const timeElapsed = currentTime - start;
          const progress = Math.min(timeElapsed / duration, 1);

          const ease = (progress) => (1 - Math.cos(progress * Math.PI)) / 2;

          window.scrollTo(0, startPosition + distance * ease(progress));

          if (progress < 1) {
            requestAnimationFrame(animation);
          }
        }

        requestAnimationFrame(animation);
      }, 150);
    }
  };
  const handleNext = () => {
    navigate(`/site-details?id=${propertyDetails?.id}&step=1`);
  };
  const logFinalSelection = async () => {
    const finalSelection = {
      panels: {
        name: selectedPanelType || null,
        recommendedBrand: selectedBrand.map((brand) => brand?.display_name),
      },
      inverter: {
        name: selectedInverterBrand ? [selectedInverterBrand?.name] : [],
      },
      structure: {
        name: selectedStructureMaterialOption?.name || null,
      },
      service: {
        name: selectedServicingOption?.name || null,
      },
    };

    try {
      if (propertyDetails?.userId) {
        await buy_preference(propertyDetails.userId, finalSelection);
        showSnackbar('Preferences saved successfully', 'success');
      } else {
        console.warn('No user ID available for saving preferences');
        showSnackbar('Unable to save preferences - no user ID', 'error');
      }
    } catch (error) {
      console.error('Error saving buy preference:', error);
      showSnackbar('Failed to save preferences', 'error');
    }
  };

  React.useEffect(() => {
    if (manualSelectionType === 'panel' && selectedPanelType) {
      scrollToSection(recommendationRef);
      setManualSelectionType(null); // Reset flag after scroll
    } else if (selectedPanelType === 'help') {
      scrollToSection(inverterSectionRef);
      setManualSelectionType(null); // Reset flag after scroll
    }
  }, [selectedPanelType, manualSelectionType]);

  React.useEffect(() => {
    if (manualSelectionType === 'brand' && selectedBrand.length >= 2) {
      scrollToSection(inverterSectionRef);
      setManualSelectionType(null); // Reset flag after scroll
    }
  }, [selectedBrand, manualSelectionType]);

  React.useEffect(() => {
    if (manualSelectionType === 'inverter' && selectedInverterBrand) {
      scrollToSection(structureSectionRef);
      setManualSelectionType(null); // Reset flag after scroll
    }
  }, [selectedInverterBrand, manualSelectionType]);

  React.useEffect(() => {
    if (
      manualSelectionType === 'structure' &&
      selectedStructureMaterialOption
    ) {
      scrollToSection(serviceSectionRef);
      setManualSelectionType(null); // Reset flag after scroll
    }
  }, [selectedStructureMaterialOption, manualSelectionType]);

  const getIconForInverterType = (key) => {
    switch (key) {
      case 'hybrid':
        return (
          <ElectricMeterRoundedIcon
            fontSize='large'
            sx={{ color: 'primary.main' }}
          />
        );
      case 'string':
        return (
          <FoundationIcon fontSize='large' sx={{ color: 'primary.main' }} />
        );
      default:
        return (
          <FoundationIcon fontSize='large' sx={{ color: 'primary.main' }} />
        );
    }
  };

  const getIconForStructureType = (key) => {
    switch (key) {
      case 'galvalume':
        return (
          <FoundationIcon fontSize='large' sx={{ color: 'primary.main' }} />
        );
      case 'hot_dip_gi':
        return (
          <CleaningServicesRoundedIcon
            fontSize='large'
            sx={{ color: 'primary.main' }}
          />
        );
      case 'pre_gi':
        return (
          <FoundationIcon fontSize='large' sx={{ color: 'primary.main' }} />
        );
      default:
        return (
          <FoundationIcon fontSize='large' sx={{ color: 'primary.main' }} />
        );
    }
  };

  const getIconForServiceType = (key) => {
    switch (key) {
      case 'full_service':
        return (
          <CleaningServicesRoundedIcon
            fontSize='large'
            sx={{ color: 'primary.main' }}
          />
        );
      case 'maintenance_only':
        return (
          <FoundationIcon fontSize='large' sx={{ color: 'primary.main' }} />
        );
      case 'no_service':
        return (
          <FoundationIcon fontSize='large' sx={{ color: 'primary.main' }} />
        );
      default:
        return (
          <FoundationIcon fontSize='large' sx={{ color: 'primary.main' }} />
        );
    }
  };

  let panelEntries = Object.entries(
    offer_selection.offer_selection.panels.technologies || {},
  );

  const getMatchingBrands = (panelType) => {
    return (offer_selection.offer_selection.panels.brands || []).filter(
      (brand) =>
        brand.technologies.some(
          (tech) => tech.type === panelType.toLowerCase(),
        ),
    );
  };

  const matchingBrands = selectedPanelType
    ? getMatchingBrands(selectedPanelType)
    : [];

  let solarPanelOptions = panelEntries.map(([key, value], index) => ({
    id: index + 1,
    powerRange: value.wattage_range || '',
    panelType:
      value.display_name ||
      key.charAt(0).toUpperCase() + key.slice(1) + ' panels',
    features: value.merits || [],
    badge: (value.tag || '').toLowerCase(),
    tagType: (value.tag_type || '').toLowerCase(),
  }));
  solarPanelOptions.push({
    id: solarPanelOptions.length + 1,
    powerRange: '',
    panelType: 'Help me choose',
    features: ['Our experts will help you choose during the visit'],
    badge: '',
    tagType: '',
  });

  const inverterOptions = (
    offer_selection.offer_selection.inverters.brands || []
  ).map((brand, index) => ({
    id: index + 1,
    name: brand.display_name || '',
    icon: brand.logo || getIconForInverterType(brand.type || ''),
    features: brand.merits || [],
    tag: (brand.tag || '').toLowerCase(),
    tagType: (brand.tag_type || '').toLowerCase(),
  }));

  const structureMaterialOptions = (
    offer_selection.offer_selection.structure_material || []
  ).map((material, index) => ({
    id: index + 1,
    name: material.display_name || '',
    description: material.description || '',
    icon: getIconForStructureType(material.type || ''),
    features: material.merits || [],
    tag: (material.tag || '').toLowerCase(),
    tagType: (material.tag_type || '').toLowerCase(),
  }));

  const servicingOptions = Object.entries(
    offer_selection.offer_selection.servicing || {},
  ).map(([key, value], index) => ({
    id: index + 1,
    name: value.display_name || '',
    description: value.description || '',
    icon: getIconForServiceType(key),
    features: value.merits || [],
    tag: (value.tag || '').toLowerCase(),
    tagType: (value.tag_type || '').toLowerCase(),
  }));

  const renderBrandLogo = (brand) => {
    const isSelected = selectedBrand.some(
      (selectedBrand) => selectedBrand.display_name === brand.display_name,
    );
    let chipProps = {};
    if (brand.tag_type === 'primary') {
      chipProps = {
        sx: {
          alignSelf: 'flex-start',
          padding: '0px 5px',
          borderRadius: '6em',
          mt: 1,
        },
        color: 'primary',
        variant: 'filled',
      };
    } else if (brand.tag_type === 'secondary') {
      chipProps = {
        sx: {
          alignSelf: 'flex-start',
          padding: '0px 5px',
          borderRadius: '6em',
          mt: 1,
          backgroundColor: theme.palette.secondary.lighter,
        },
        color: 'secondary',
        variant: 'filled',
      };
    } else {
      chipProps = {
        sx: {
          backgroundColor: 'transparent',
          border: '1px solid gray',
          alignSelf: 'flex-start',
          padding: '0px 5px',
          borderRadius: '6em',
          mt: 1,
        },
        variant: 'outlined',
      };
    }
    return (
      <Card
        onClick={() => {
          if (isSelected) {
            // Remove brand if already selected
            setSelectedBrand((prev) =>
              prev.filter((b) => b.display_name !== brand.display_name),
            );
          } else {
            // Add brand to selection
            setSelectedBrand((prev) => [...prev, brand]);
            setManualSelectionType('brand'); // Set specific selection type
          }
        }}
        sx={{
          cursor: 'pointer',
          width: '100%',
          // marginTop: 2,
          borderRadius: '16px',
          border: '1px solid lightgray',
          backgroundColor: isSelected ? '#C8E6C9' : 'white',
          boxShadow: 'none',
          maxWidth: '500px',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow:
              '0 1px 2px rgba(20, 17, 17, 0.12), 0 1px 10px rgba(0,0,0,0.24)',
            transform: 'translateY(-4px)',
          },
        }}
      >
        <Tooltip title={brand.display_name} placement='top-start' arrow>
          <CardActionArea>
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  width='100%'
                  alignItems='flex-start'
                >
                  <Box
                    component='img'
                    src={
                      brandImageMap[brand.display_name?.toLowerCase()] ||
                      brand.logo
                    }
                    alt={brand.display_name}
                    sx={{ width: '100%', maxWidth: 120, mb: 1 }}
                  />
                  {isSelected && (
                    <CheckCircleIcon
                      sx={{ color: theme.palette.secondary.light }}
                    />
                  )}
                </Stack>
                {brand.technologies
                  .filter((tech) => tech.type === selectedPanelType)
                  .map((tech, idx) => (
                    <Box key={idx}>
                      <Typography
                        variant='body2'
                        fontWeight='bold'
                        mb={1}
                        color='gray'
                      >
                        {tech.dcr_type === 'both'
                          ? 'DCR & Non-DCR'
                          : tech.dcr_type}{' '}
                        modules
                      </Typography>
                      {tech.merits && (
                        <Typography
                          variant='body2'
                          fontWeight='bold'
                          mb={1}
                          color='gray'
                        >
                          {tech.merits?.[0]}
                        </Typography>
                      )}
                      <Stack justifyContent={'space-between'} direction='row'>
                        {tech.datasheet_link && (
                          <Link
                            variant='body2'
                            href={tech.datasheet_link}
                            target='_blank'
                            rel='noopener noreferrer'
                            sx={{
                              color: theme.palette.primary.main,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            View datasheet
                          </Link>
                        )}
                        {brand.tag && (
                          <Chip
                            label={brand.tag}
                            size='small'
                            sx={{}}
                            {...chipProps}
                          />
                        )}
                      </Stack>
                    </Box>
                  ))}
              </Stack>
            </CardContent>
          </CardActionArea>
        </Tooltip>
      </Card>
    );
  };

  const renderBrandsGrid = () => {
    if (selectedPanelType && matchingBrands.length > 0) {
      return (
        <Stack
          width='100%'
          mt={8}
          ref={recommendationRef}
          sx={{
            transition: 'all 0.3s ease-in-out',
            opacity: 1,
            transform: 'translateY(0)',
            '&:not(:in-view)': {
              opacity: 0.8,
              transform: 'translateY(20px)',
            },
          }}
          gap={2}
        >
          <Typography
            mb={2}
            sx={{
              fontSize: {
                xs: theme.typography.h6.fontSize,
                md: theme.typography.h5.fontSize,
              },
              fontWeight: 'bold',
            }}
          >
            Recommended brands.{' '}
            <Typography
              color='gray'
              sx={{
                fontSize: {
                  xs: theme.typography.h6.fontSize,
                  md: theme.typography.h5.fontSize,
                },
                fontWeight: 'bold',

                display: 'inline',
              }}
            >
              Select two brands.
            </Typography>
          </Typography>
          <Grid container spacing={2}>
            {matchingBrands.map((brand, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                {renderBrandLogo(brand)}
              </Grid>
            ))}
          </Grid>
        </Stack>
      );
    }
    return null;
  };

  return (
    <Stack alignItems='flex-start' mt={4} mb={12}>
      <Typography
        display={'flex'}
        alignItems={'center'}
        mb={4}
        justifyContent={'flex-start'}
        sx={{
          color: 'gray',
          fontSize: {
            xs: theme.typography.h3.fontSize,
            md: theme.typography.h2.fontSize,
          },
          fontWeight: 'bold',
        }}
      >
        Pick my solar solution
      </Typography>
      <Typography
        display={'flex'}
        alignItems={'center'}
        justifyContent={'flex-start'}
        sx={{
          fontSize: {
            xs: theme.typography.h5.fontSize,
            md: theme.typography.h4.fontSize,
          },
          fontWeight: 'bold',
        }}
      >
        <SolarPowerRoundedIcon
          sx={{
            mr: 2,
            color: theme.palette.primary.main,
            fontSize: 'inherit',
          }}
        />
        Panel technologies
      </Typography>
      <Stack
        ref={panelSectionRef}
        alignItems={isMobile ? 'flex-start' : 'unset'}
        mt={3}
        flexDirection={isMobile ? 'column' : 'row'}
        width={'100%'}
        gap={isMobile ? '8px' : '16px'}
        sx={{
          transition: 'all 0.3s ease-in-out',
          opacity: 1,
          transform: 'translateY(0)',
          '&:not(:in-view)': {
            opacity: 0.8,
            transform: 'translateY(20px)',
          },
        }}
      >
        {solarPanelOptions.map((panelOption) => {
          let chipProps = {};
          if (panelOption.tagType === 'primary') {
            chipProps = {
              sx: {
                alignSelf: 'flex-start',
                padding: '0px 5px',
                borderRadius: '6em',
                mt: 1,
              },
              color: 'primary',
              variant: 'filled',
            };
          } else if (panelOption.tagType === 'secondary') {
            chipProps = {
              sx: {
                alignSelf: 'flex-start',
                padding: '0px 5px',
                borderRadius: '6em',
                mt: 1,
                backgroundColor: theme.palette.secondary.lighter,
              },
              color: 'secondary',
              variant: 'filled',
            };
          } else {
            chipProps = {
              sx: {
                backgroundColor: 'transparent',
                border: '1px solid gray',
                alignSelf: 'flex-start',
                padding: '0px 5px',
                borderRadius: '6em',
                mt: 1,
              },
              variant: 'outlined',
            };
          }
          return (
            <Card
              key={panelOption.id}
              sx={{
                cursor: 'pointer',
                width: '100%',
                marginTop: 2,
                borderRadius: '16px',
                border: '1px solid lightgray',
                backgroundColor:
                  selectedPanelType ===
                  panelOption.panelType.split(' ')[0].toLowerCase()
                    ? theme.palette.secondary.lighter
                    : 'white',
                boxShadow: 'none',
                maxWidth: '500px',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  boxShadow:
                    '0 1px 2px rgba(20, 17, 17, 0.12), 0 1px 10px rgba(0,0,0,0.24)',
                  transform: 'translateY(-4px)',
                },
              }}
              onClick={() => {
                if (panelOption.panelType === '') {
                  setSelectedPanelType(null);
                } else {
                  const type = panelOption.panelType
                    .toLowerCase()
                    .split(' ')[0];
                  setSelectedPanelType(type);
                  setManualSelectionType('panel'); // Set specific selection type
                }
              }}
            >
              <CardActionArea sx={{ display: 'flex', flex: 1, height: '100%' }}>
                <CardContent sx={{ display: 'flex', flex: 1, height: '100%' }}>
                  <Stack
                    justifyContent='space-between'
                    height='100%'
                    width={'100%'}
                  >
                    <Box>
                      <Stack
                        justifyContent={'space-between'}
                        flexDirection={'row'}
                        alignItems='flex-start'
                        width='100%'
                        mt={2}
                      >
                        <Stack spacing={1}>
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              fontSize: {
                                xs: theme.typography.body1.fontSize,
                                md: theme.typography.body1.fontSize,
                              },
                            }}
                          >
                            {panelOption.powerRange}
                          </Typography>
                          <Typography
                            sx={{
                              color: theme.palette.secondary.light,
                              fontSize: {
                                xs: theme.typography.h5.fontSize,
                                md: theme.typography.h5.fontSize,
                              },
                              fontWeight: 'bold',
                            }}
                          >
                            {panelOption.panelType}
                          </Typography>
                        </Stack>
                        {selectedPanelType ===
                          panelOption.panelType.split(' ')[0].toLowerCase() && (
                          <CheckCircleIcon
                            sx={{ color: theme.palette.secondary.light }}
                          />
                        )}
                      </Stack>
                    </Box>
                    <Stack>
                      <Stack mt={4}>
                        {panelOption.features.map((feature, index) => (
                          <Typography
                            key={index}
                            sx={{
                              fontWeight: 'bold',
                              fontSize: {
                                xs: theme.typography.body2.fontSize,
                                md: theme.typography.body2.fontSize,
                              },
                              color: 'gray',
                              mb: 0.5,
                            }}
                          >
                            {feature}
                          </Typography>
                        ))}
                      </Stack>

                      {panelOption.badge && (
                        <Chip
                          label={panelOption.badge}
                          size='small'
                          sx={{}}
                          {...chipProps}
                        />
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Stack>

      {renderBrandsGrid()}

      <Stack
        width='100%'
        mt={12}
        ref={inverterSectionRef}
        sx={{
          transition: 'all 0.3s ease-in-out',
          opacity: 1,
          transform: 'translateY(0)',
          '&:not(:in-view)': {
            opacity: 0.8,
            transform: 'translateY(20px)',
          },
        }}
      >
        <Typography
          display={'flex'}
          alignItems={'center'}
          justifyContent={'flex-start'}
          sx={{
            fontSize: {
              xs: theme.typography.h5.fontSize,
              md: theme.typography.h4.fontSize,
            },
            fontWeight: 'bold',
          }}
        >
          <ElectricMeterRoundedIcon
            sx={{
              mr: 2,
              color: theme.palette.primary.main,
              fontSize: 'inherit',
            }}
          />
          Inverter options
        </Typography>
        <Stack
          alignItems={isMobile ? 'flex-start' : 'unset'}
          mt={3}
          flexDirection={isMobile ? 'column' : 'row'}
          width={'100%'}
          gap={isMobile ? '8px' : '16px'}
        >
          {inverterOptions.map((inverter) => {
            const isSelected = selectedInverterBrand?.name === inverter.name;
            let chipProps = {};
            if (inverter.tagType === 'primary') {
              chipProps = {
                sx: {
                  alignSelf: 'flex-start',
                  padding: '0px 5px',
                  borderRadius: '6em',
                  mt: 1,
                },
                color: 'primary',
                variant: 'filled',
              };
            } else if (inverter.tagType === 'secondary') {
              chipProps = {
                sx: {
                  alignSelf: 'flex-start',
                  padding: '0px 5px',
                  borderRadius: '6em',
                  mt: 1,
                  backgroundColor: theme.palette.secondary.lighter,
                },
                color: 'secondary',
                variant: 'filled',
              };
            } else if (inverter.badge) {
              chipProps = {
                sx: {
                  backgroundColor: 'transparent',
                  border: '1px solid gray',
                  alignSelf: 'flex-start',
                  padding: '0px 5px',
                  borderRadius: '6em',
                  mt: 1,
                },
                variant: 'outlined',
              };
            }
            return (
              <Card
                key={inverter.id}
                sx={{
                  cursor: 'pointer',
                  width: '100%',
                  marginTop: 2,
                  borderRadius: '16px',
                  border: '1px solid lightgray',
                  backgroundColor: isSelected ? '#C8E6C9' : 'white',
                  boxShadow: 'none',
                  maxWidth: '300px',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow:
                      '0 1px 2px rgba(20, 17, 17, 0.12), 0 1px 10px rgba(0,0,0,0.24)',
                    transform: 'translateY(-4px)',
                  },
                }}
                onClick={() => {
                  setSelectedInverterBrand(inverter);
                  setManualSelectionType('inverter'); // Set specific selection type
                }}
              >
                <Tooltip title={inverter.name} placement='top-start' arrow>
                  <CardActionArea
                    sx={{ display: 'flex', flex: 1, height: '100%' }}
                  >
                    <CardContent
                      sx={{ display: 'flex', flex: 1, height: '100%', p: 2 }}
                    >
                      <Stack
                        justifyContent='space-between'
                        height='100%'
                        width='100%'
                      >
                        <Box>
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            width='100%'
                          >
                            <Stack
                              direction='column'
                              alignItems='flex-start'
                              mt={12}
                            >
                              <img
                                src={inverter.icon}
                                alt={inverter.name}
                                height='42px'
                              />
                            </Stack>
                            {isSelected && (
                              <CheckCircleIcon
                                sx={{ color: theme.palette.secondary.light }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <Stack>
                          <Stack mt={4}>
                            {inverter.features.map((feature, index) => (
                              <Typography
                                key={index}
                                sx={{
                                  fontWeight: 'bold',
                                  fontSize: {
                                    xs: theme.typography.body2.fontSize,
                                    md: theme.typography.body2.fontSize,
                                  },
                                  color: 'gray',
                                  mb: 0.5,
                                }}
                              >
                                {feature}
                              </Typography>
                            ))}
                          </Stack>

                          {inverter.tag && (
                            <Chip
                              label={inverter.tag}
                              size='small'
                              {...chipProps}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Tooltip>
              </Card>
            );
          })}
        </Stack>
      </Stack>

      <Stack width='100%' mt={12} ref={structureSectionRef}>
        <Typography
          display={'flex'}
          alignItems={'center'}
          justifyContent={'flex-start'}
          sx={{
            fontSize: {
              xs: theme.typography.h5.fontSize,
              md: theme.typography.h4.fontSize,
            },
            fontWeight: 'bold',
          }}
        >
          <FoundationIcon
            sx={{
              mr: 2,
              color: theme.palette.primary.main,
              fontSize: 'inherit',
            }}
          />
          Structure material
        </Typography>
        <Stack
          alignItems={isMobile ? 'flex-start' : 'unset'}
          mt={3}
          flexDirection={isMobile ? 'column' : 'row'}
          width={'100%'}
          gap={isMobile ? '8px' : '16px'}
        >
          {structureMaterialOptions.map((material) => {
            const isSelected =
              selectedStructureMaterialOption?.name === material.name;
            let chipProps = {};
            if (material.tagType === 'primary') {
              chipProps = {
                sx: {
                  alignSelf: 'flex-start',
                  padding: '0px 5px',
                  borderRadius: '6em',
                  mt: 1,
                },
                color: 'primary',
                variant: 'filled',
              };
            } else if (material.tagType === 'secondary') {
              chipProps = {
                sx: {
                  alignSelf: 'flex-start',
                  padding: '0px 5px',
                  borderRadius: '6em',
                  mt: 1,
                  backgroundColor: theme.palette.secondary.lighter,
                },
                color: 'secondary',
                variant: 'filled',
              };
            } else {
              chipProps = {
                sx: {
                  backgroundColor: 'transparent',
                  border: '1px solid gray',
                  alignSelf: 'flex-start',
                  padding: '0px 5px',
                  borderRadius: '6em',
                  mt: 1,
                },
                variant: 'outlined',
              };
            }
            return (
              <Card
                key={material.id}
                sx={{
                  cursor: 'pointer',
                  width: '100%',
                  marginTop: 2,
                  borderRadius: '16px',
                  border: '1px solid lightgray',
                  backgroundColor: isSelected ? '#C8E6C9' : 'white',
                  boxShadow: 'none',
                  maxWidth: '300px',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow:
                      '0 1px 5px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                    transform: 'translateY(-4px)',
                  },
                }}
                onClick={() => {
                  setSelectedStructureMaterialOption(material);
                  setManualSelectionType('structure'); // Set specific selection type
                }}
              >
                <CardActionArea>
                  <CardContent
                    sx={{
                      display: 'flex',
                      flex: 1,
                      height: '100%',
                    }}
                  >
                    <Stack
                      justifyContent='space-between'
                      height='100%'
                      width='100%'
                      mt={12}
                    >
                      <Box>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                          width='100%'
                        >
                          <Typography
                            sx={{
                              fontSize: {
                                xs: theme.typography.h5.fontSize,
                                md: theme.typography.h5.fontSize,
                              },
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {/* {material.icon} */}
                            {material?.name}
                          </Typography>
                          {isSelected && (
                            <CheckCircleIcon
                              sx={{ color: theme.palette.secondary.light }}
                            />
                          )}
                        </Stack>
                      </Box>
                      <Stack>
                        <Stack mt={2}>
                          {material.features.map((feature, index) => (
                            <Typography
                              key={index}
                              sx={{
                                fontWeight: 'bold',
                                fontSize: {
                                  xs: theme.typography.body2.fontSize,
                                  md: theme.typography.body2.fontSize,
                                },
                                color: 'gray',
                                mb: 0.5,
                              }}
                            >
                              {feature}
                            </Typography>
                          ))}
                        </Stack>

                        {material.tag && (
                          <Chip
                            label={material.tag}
                            size='small'
                            {...chipProps}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      </Stack>

      <Stack width='100%' mt={12} ref={serviceSectionRef}>
        <Typography
          display={'flex'}
          alignItems={'center'}
          justifyContent={'flex-start'}
          sx={{
            fontSize: {
              xs: theme.typography.h5.fontSize,
              md: theme.typography.h4.fontSize,
            },
            fontWeight: 'bold',
          }}
        >
          <CleaningServicesRoundedIcon
            sx={{
              mr: 2,
              color: theme.palette.primary.main,
              fontSize: 'inherit',
            }}
          />
          Service and maintenance
        </Typography>
        <Stack
          alignItems={isMobile ? 'flex-start' : 'unset'}
          mt={3}
          flexDirection={isMobile ? 'column' : 'row'}
          width={'100%'}
          gap={isMobile ? '8px' : '16px'}
        >
          {servicingOptions.map((service) => {
            const isSelected = selectedServicingOption?.name === service.name;
            let chipProps = {};
            if (service.tagType === 'primary') {
              chipProps = {
                sx: {
                  alignSelf: 'flex-start',
                  padding: '0px 5px',
                  borderRadius: '6em',
                  mt: 1,
                },
                color: 'primary',
                variant: 'filled',
              };
            } else if (service.tagType === 'secondary') {
              chipProps = {
                sx: {
                  alignSelf: 'flex-start',
                  padding: '0px 5px',
                  borderRadius: '6em',
                  mt: 1,
                  backgroundColor: theme.palette.secondary.lighter,
                },
                color: 'secondary',
                variant: 'filled',
              };
            } else {
              chipProps = {
                sx: {
                  backgroundColor: 'transparent',
                  border: '1px solid gray',
                  alignSelf: 'flex-start',
                  padding: '0px 5px',
                  borderRadius: '6em',
                  mt: 1,
                },
                variant: 'outlined',
              };
            }
            return (
              <Card
                key={service.id}
                sx={{
                  cursor: 'pointer',
                  width: '100%',
                  marginTop: 2,
                  borderRadius: '16px',
                  border: '1px solid lightgray',
                  backgroundColor: isSelected ? '#C8E6C9' : 'white',
                  boxShadow: 'none',
                  maxWidth: '300px',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow:
                      '0 1px 5px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                    transform: 'translateY(-4px)',
                  },
                }}
                onClick={() => {
                  setSelectedServicingOption(service);
                }}
              >
                <CardActionArea>
                  <CardContent
                    sx={{ display: 'flex', flex: 1, height: '100%' }}
                  >
                    <Stack
                      justifyContent='space-between'
                      height='100%'
                      width={'100%'}
                      mt={12}
                    >
                      <Box>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                          width='100%'
                        >
                          <Typography
                            sx={{
                              fontSize: {
                                xs: theme.typography.h5.fontSize,
                                md: theme.typography.h5.fontSize,
                              },
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {service.name}
                          </Typography>
                          {isSelected && (
                            <CheckCircleIcon
                              sx={{ color: theme.palette.secondary.light }}
                            />
                          )}
                        </Stack>

                        <Stack mb={2} mt={2}>
                          {service.features.map((feature, index) => (
                            <Typography
                              key={index}
                              sx={{
                                fontWeight: 'bold',
                                fontSize: {
                                  xs: theme.typography.body2.fontSize,
                                  md: theme.typography.body2.fontSize,
                                },
                                color: 'gray',
                                mb: 0.5,
                              }}
                            >
                              {feature}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>

                      {service.tag && (
                        <Chip label={service.tag} size='small' {...chipProps} />
                      )}
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      </Stack>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent={'flex-end'}
        pt={8}
        width={'100%'}
      >
        <Button
          variant='contained'
          disableElevation
          sx={{ textTransform: 'none' }}
          onClick={async () => {
            try {
              await logFinalSelection();

              if (isLoggedIn()) {
                handleQuote(66, propertyDetails?.userId);
              } else {
                handleRequestCall();
              }
            } catch (error) {
              console.error('Error in button click handler:', error);
              showSnackbar('An error occurred. Please try again.', 'error');
            }
          }}
          startIcon={quoteStatus ? <Check /> : <FeedIcon />}
          size={isMobile ? 'small' : 'default'}
        >
          Request quote
        </Button>
      </Stack>
      <SiteVisitDialog open={open} setOpen={setOpen} handleNext={handleNext} />
      <LoginMiniForm
        open={isLoginFormDialogOpen}
        handleClose={closeLoginForm}
        vendorId={66}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

export default PanelTechnologies;
